// ============================================================================
// CANVAS INSTANCE NAVIGATOR
// ============================================================================
// Tab bar for switching between canvas instances of a template

import { useState, useRef, useEffect } from 'react'
import { Plus, MoreHorizontal, Copy, Trash2, Edit3, Loader2, Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCanvasInstances, useDeleteCanvasInstance, useDuplicateCanvasInstance, useUpdateCanvasInstance } from '@/hooks/useCanvasInstances'
import type { CanvasInstanceSummary } from '@/types/canvas'

interface CanvasInstanceNavigatorProps {
  templateId: string
  selectedInstanceUri: string | null
  onSelectInstance: (uri: string, name: string) => void
  onCreateNew: () => void
}

export function CanvasInstanceNavigator({
  templateId,
  selectedInstanceUri,
  onSelectInstance,
  onCreateNew,
}: CanvasInstanceNavigatorProps) {
  const { data: instances = [], isLoading } = useCanvasInstances(templateId)
  const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null)
  const [editingUri, setEditingUri] = useState<string | null>(null)

  const deleteInstance = useDeleteCanvasInstance()
  const duplicateInstance = useDuplicateCanvasInstance()
  const updateInstance = useUpdateCanvasInstance()

  const handleDelete = async (instance: CanvasInstanceSummary) => {
    if (!confirm(`Delete "${instance.name}"? This cannot be undone.`)) return
    await deleteInstance.mutateAsync(instance.uri)
    setMenuOpenFor(null)
  }

  const handleDuplicate = async (instance: CanvasInstanceSummary) => {
    const newUri = await duplicateInstance.mutateAsync({
      uri: instance.uri,
      newName: `${instance.name} (copy)`,
    })
    setMenuOpenFor(null)
    onSelectInstance(newUri, `${instance.name} (copy)`)
  }

  const handleStartEdit = (instance: CanvasInstanceSummary) => {
    setMenuOpenFor(null)
    setEditingUri(instance.uri)
  }

  const handleSaveEdit = async (uri: string, newName: string) => {
    if (newName.trim()) {
      await updateInstance.mutateAsync({
        uri,
        updates: { name: newName.trim() },
      })
      // Update selection name if this is the selected instance
      if (selectedInstanceUri === uri) {
        onSelectInstance(uri, newName.trim())
      }
    }
    setEditingUri(null)
  }

  const handleCancelEdit = () => {
    setEditingUri(null)
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 h-10 px-2">
        <Loader2 className="w-4 h-4 animate-spin text-text-muted" />
        <span className="text-sm text-text-muted">Loading canvases...</span>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1 min-h-[40px] border-b border-warm-200 bg-warm-50/50 px-2 overflow-x-auto">
      {/* Instance Tabs */}
      {instances.map((instance) => (
        <InstanceTab
          key={instance.uri}
          instance={instance}
          isSelected={selectedInstanceUri === instance.uri}
          isEditing={editingUri === instance.uri}
          onSelect={() => onSelectInstance(instance.uri, instance.name)}
          menuOpen={menuOpenFor === instance.uri}
          onMenuToggle={() => setMenuOpenFor(menuOpenFor === instance.uri ? null : instance.uri)}
          onDelete={() => handleDelete(instance)}
          onDuplicate={() => handleDuplicate(instance)}
          onEdit={() => handleStartEdit(instance)}
          onSaveEdit={(newName) => handleSaveEdit(instance.uri, newName)}
          onCancelEdit={handleCancelEdit}
        />
      ))}

      {/* Create New Button */}
      <button
        onClick={onCreateNew}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors',
          'text-solution hover:bg-solution/10 border border-dashed border-solution/30'
        )}
      >
        <Plus className="w-4 h-4" />
        <span>New</span>
      </button>

      {/* Empty State */}
      {instances.length === 0 && (
        <span className="text-sm text-text-muted ml-2">
          No canvases yet. Create your first one!
        </span>
      )}
    </div>
  )
}

interface InstanceTabProps {
  instance: CanvasInstanceSummary
  isSelected: boolean
  isEditing: boolean
  onSelect: () => void
  menuOpen: boolean
  onMenuToggle: () => void
  onDelete: () => void
  onDuplicate: () => void
  onEdit: () => void
  onSaveEdit: (newName: string) => void
  onCancelEdit: () => void
}

function InstanceTab({
  instance,
  isSelected,
  isEditing,
  onSelect,
  menuOpen,
  onMenuToggle,
  onDelete,
  onDuplicate,
  onEdit,
  onSaveEdit,
  onCancelEdit,
}: InstanceTabProps) {
  const [editName, setEditName] = useState(instance.name)
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Reset edit name when editing starts
  useEffect(() => {
    if (isEditing) {
      setEditName(instance.name)
    }
  }, [isEditing, instance.name])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSaveEdit(editName)
    } else if (e.key === 'Escape') {
      onCancelEdit()
    }
  }

  if (isEditing) {
    return (
      <div className="relative flex items-center gap-1 bg-white rounded-t-lg px-1 py-1 border-b-2 border-solution shadow-sm">
        <input
          ref={inputRef}
          type="text"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => onSaveEdit(editName)}
          className="w-[140px] px-2 py-1 text-sm border border-warm-300 rounded focus:outline-none focus:ring-2 focus:ring-solution/20 focus:border-solution"
        />
        <button
          onClick={() => onSaveEdit(editName)}
          className="p-1 rounded hover:bg-warm-100 text-solution"
          title="Save"
        >
          <Check className="w-4 h-4" />
        </button>
        <button
          onClick={onCancelEdit}
          className="p-1 rounded hover:bg-warm-100 text-text-muted"
          title="Cancel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    )
  }

  return (
    <div className="relative flex items-center group">
      <button
        onClick={onSelect}
        onDoubleClick={onEdit}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 text-sm rounded-t-lg transition-colors border-b-2',
          isSelected
            ? 'bg-white text-text font-medium border-solution shadow-sm'
            : 'text-text-secondary hover:bg-warm-100 border-transparent'
        )}
        title="Double-click to rename"
      >
        <span className="max-w-[150px] truncate">{instance.name}</span>
        {instance.totalLinkedItems > 0 && (
          <span
            className={cn(
              'text-xs px-1.5 py-0.5 rounded-full',
              isSelected ? 'bg-solution/10 text-solution' : 'bg-warm-200 text-text-muted'
            )}
          >
            {instance.totalLinkedItems}
          </span>
        )}
      </button>

      {/* Menu Button - visible on hover or when selected */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onMenuToggle()
        }}
        className={cn(
          'p-1 rounded transition-opacity',
          'hover:bg-warm-200 text-text-muted hover:text-text',
          isSelected || menuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        )}
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>

      {/* Dropdown Menu */}
      {menuOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={onMenuToggle} />

          <div className="absolute right-0 top-full mt-1 w-36 bg-white rounded-lg shadow-lg border border-warm-200 py-1 z-50">
            <button
              onClick={onEdit}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-text hover:bg-warm-100 transition-colors"
            >
              <Edit3 className="w-4 h-4" />
              Rename
            </button>
            <button
              onClick={onDuplicate}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-text hover:bg-warm-100 transition-colors"
            >
              <Copy className="w-4 h-4" />
              Duplicate
            </button>
            <hr className="my-1 border-warm-200" />
            <button
              onClick={onDelete}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  )
}
